import styled from "@emotion/styled"
import React from "react"
import { Seo } from "gatsby-theme-grape-blog/src/components/seo"
import { Brand } from "../components/brand"
import Layout from "../components/layout"

import heroImage from "../../content/assets/hero.jpg"
import patreonPreview from "../../content/assets/patreon.png"

// Brand Colors
const colors = {
  brandPrimary: "#126782", // Dark teal/blue (free section)
  brandPrimaryDark: "#3a7b7d", // Darker teal (borders, accents)
  brandPrimaryLight: "#8ecae6", // Light blue (free section links)
  brandSecondary: "#fbed28", // Orange (premium section)
  textPrimary: "#023047", // Dark text
  textSecondary: "#2c3e50", // Medium dark text
  textLight: "white", // White text
}

const links = {
  instagram: "https://www.instagram.com/evidence_strong",
  youtube: "https://www.youtube.com/@evidencestrong",
  podcast: "https://rss.com/podcasts/evidence-strong",
  patreon: "https://www.patreon.com/evidencestrong",
  newsletter: "/newsletter",
  blog: "/blog",
  speakingInvite: "/contact/",
}

const HomePageLayout = styled(Layout)`
  h2 {
    font-size: 3rem;
    text-align: center;
    margin-bottom: 3rem;
    margin-top: 0.5rem;
    font-weight: bold;
  }
`

const FullWidthHeroImage = styled.img`
  display: block;
  width: 100vw;
  max-width: 100vw;
  height: auto;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  margin-bottom: 0;

  @media (max-width: 768px) {
    min-width: 150%;
    margin-left: -40%;
  }
`

const HeroWrapper = styled.div`
  position: relative;
  overflow: hidden;
`

const HeroBrand = styled(Brand)`
  position: absolute;
  top: 1rem;
  left: 3rem;
  z-index: 2;
  text-decoration: none;

  &,
  & * {
    color: #ffffff !important;
  }

  h1 {
    margin-bottom: 0;
  }

  img {
    filter: brightness(0) invert(1);
  }
`

const HeroEventsCallout = styled.a`
  position: absolute;
  left: 3rem;
  bottom: 1.25rem;
  z-index: 2;
  display: block;
  width: min(22rem, calc(100% - 3rem));
  padding: 0.85rem 1rem;
  color: #ffffff;
  background: rgba(2, 48, 71, 0.88);
  border-left: 5px solid ${colors.brandSecondary};
  border-radius: 8px;
  text-decoration: none;
  background-image: none;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.28);

  &:hover {
    background: rgba(2, 48, 71, 0.96);
    background-image: none;
  }

  @media (max-width: 768px) {
    left: 1.5rem;
    bottom: 1rem;
    width: calc(100% - 3rem);
    padding: 0.75rem 0.9rem;
  }
`

const HeroEventsLabel = styled.span`
  display: block;
  margin-bottom: 0.2rem;
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1;
  text-transform: uppercase;
`

const HeroEventsText = styled.span`
  display: block;
  color: ${colors.brandSecondary};
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.2;
`

const MainContent = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
  min-height: 70vh;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const LeftColumn = styled.div`
  background: ${colors.brandPrimary};
  color: ${colors.textLight};
  display: flex;
  justify-content: center;

  h2,
  h3 {
    color: ${colors.textLight};
  }
`

const RightColumn = styled.div`
  background: ${colors.brandSecondary};
  color: ${colors.textPrimary};
  display: flex;
  justify-content: center;
  height: 100%;
`

const ColumnContent = styled.div`
  max-width: 600px;
  width: 100%;
  padding: 2rem 1.5rem;

  @media (max-width: 768px) {
    min-height: 0;
    padding: 2rem;
    max-width: 100%;
  }
`

const PatreonPreview = styled.a`
  display: block;
  margin-top: 1rem;
  background-image: none;
`

const PatreonPreviewImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
`

const ResourceBlock = styled.div`
  margin-bottom: 3rem;

  h3 {
    font-size: 1.5rem;
    font-weight: bold;
    margin-bottom: 1rem;
    text-transform: uppercase;
  }

  p {
    margin-bottom: 1rem;
    line-height: 1.6;
  }

  ul {
    margin-bottom: 1rem;
    padding-left: 0.5rem;

    li {
      margin-bottom: 0.5rem;
    }
  }
`

const FeaturedResourceBlock = styled(ResourceBlock)`
  width: calc(100% + 1.5rem + 12px);
  margin-top: calc(-0.75rem);
  margin-left: calc(-0.75rem - 6px);
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.55);
  border-left: 6px solid ${colors.brandPrimary};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(2, 48, 71, 0.12);

  h3 {
    margin-top: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    width: calc(100% + 4rem);
    margin-left: -2rem;
    padding: 1.5rem 2rem;
    border-left: 0;
    border-radius: 0;
  }
`

const FreeSectionLink = styled.a`
  color: ${colors.brandPrimaryLight};
  text-decoration: underline;
  font-weight: 500;

  &:hover {
    opacity: 0.8;
  }
`

const PremiumSectionLink = styled.a`
  color: ${colors.brandPrimary};
  text-decoration: underline;
  font-weight: 700;

  &:hover {
    opacity: 0.8;
  }
`

const TopSection = styled.div`
  padding: 2rem 3rem;
  background: #fff;

  h1 {
    display: none;
    text-transform: uppercase;
    font-size: 2rem;
    text-align: center;
    margin-bottom: 1rem;
    margin-top: 0.5rem;
    font-weight: bold;
  }

  h2 {
    display: none;
    font-size: 1.5rem;
  }

  @media (max-width: 1400px) {
    padding: 2rem 1.5rem;
  }

  @media (max-width: 768px) {
    h1,
    h2 {
      display: block;
    }
  }
`

const Testimonials = styled.div`
  display: flex;
  flex-direction: row;
  gap: 3.5rem;
  align-items: stretch;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 1400px) {
    gap: 1.5rem;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`

const TestimonialCard = styled.blockquote`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: ${colors.textLight};
  color: ${colors.textSecondary};
  padding: 1.25rem;
  border-left: 6px solid ${colors.brandPrimaryDark};
  margin: 0;
  flex: 1 1 200px;
  border-radius: 10px;
  border: 1px solid rgba(2, 48, 71, 0.06);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
  transition: transform 120ms ease, box-shadow 120ms ease;

  @media (max-width: 768px) {
    flex: 0;
  }

  p {
    font-size: 0.9rem;
    font-style: italic;
    margin-bottom: 0.5rem;
    line-height: 1.4;
    position: relative;
    min-height: 6rem;

    @media (max-width: 900px) {
      font-size: 0.8rem;
    }

    @media (max-width: 768px) {
      min-height: 0;
      font-size: 1rem;
    }
  }

  p::before {
    content: "“";
    position: absolute;
    left: -0.75rem;
    top: -0.25rem;
    color: ${colors.brandPrimaryDark};
    font-size: 1.5rem;
    line-height: 1;
  }

  p::after {
    content: "”";
    position: absolute;
    color: ${colors.brandPrimaryDark};
    font-size: 1.5rem;
    line-height: 1;
  }

  cite {
    font-weight: 600;
    font-style: normal;
    font-size: 1rem;
    letter-spacing: 0.01em;
    color: ${colors.textSecondary};
    opacity: 0.9;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.08);
  }
`

const HomePage = () => {
  return (
    <HomePageLayout>
      <Seo title="Evidence Strong - Where Science Meets Performance" />
      <HeroWrapper>
        <FullWidthHeroImage src={heroImage} alt="Evidence Strong hero" />
        <HeroBrand size="small" />
        <HeroEventsCallout href="/events/">
          <HeroEventsLabel>Upcoming events</HeroEventsLabel>
          <HeroEventsText>See what’s next →</HeroEventsText>
        </HeroEventsCallout>
      </HeroWrapper>

      <TopSection>
        <h1>Evidence Strong</h1>
        <h2>where science meets performance</h2>
        <Testimonials>
          <TestimonialCard>
            <p>
              Thanks for the work you put in. It saves me a lot of time not
              having to do the research. One topic I would be interested in is
              training for weightlifting. There is a lot of research on strength
              training, but the question is what is actually relevant for
              weightlifting.
            </p>
            <cite>Patreon Member</cite>
          </TestimonialCard>
          <TestimonialCard>
            <p>
              I really appreciate your keenness for the sport and all that
              encompasses being a Masters Olympic weightlifter. Your role as a
              conduit for delivering information on strength, power,
              weightlifting and more is fantastic. Keep up the good work.
            </p>
            <cite>Podcast Guest</cite>
          </TestimonialCard>
          <TestimonialCard>
            <p>
              I have been a fan and follower of Evidence Strong for a while now.
              I love everything you are doing with the research and
              accessibility. I share your content frequently with my coaching
              staff and athletes. I will happily continue to support in any way
              I can.
            </p>
            <cite>Coach</cite>
          </TestimonialCard>
        </Testimonials>
      </TopSection>

      <MainContent>
        <LeftColumn>
          <ColumnContent>
            <h2>FREE resources</h2>

            <ResourceBlock>
              <h3>Podcast</h3>
              <p>
                Join expert conversations on the latest research and coaching
                trends in strength sports. Each episode delivers actionable,
                science-driven tips for enhancing your coaching and training.
              </p>
              <p>
                →{" "}
                <FreeSectionLink href={links.podcast}>
                  Listen to podcast
                </FreeSectionLink>{" "}
                or{" "}
                <FreeSectionLink href={links.youtube}>
                  watch on Youtube
                </FreeSectionLink>
              </p>
            </ResourceBlock>

            <ResourceBlock>
              <h3>Newsletter</h3>
              <p>
                Sign up for monthly updates on new episodes and upcoming events.
              </p>
              <p>
                →{" "}
                <FreeSectionLink href={links.newsletter}>
                  Sign up for the newsletter
                </FreeSectionLink>
              </p>
            </ResourceBlock>

            <ResourceBlock>
              <h3>Episode Summaries</h3>
              <p>
                No time to listen full podcast episode? Browse free summaries on
                Patreon's free tier. Join to start reading.
              </p>
              <p>
                →{" "}
                <FreeSectionLink href={links.patreon}>
                  Join for free
                </FreeSectionLink>
              </p>
            </ResourceBlock>

            <ResourceBlock>
              <h3>Blog & Infographics</h3>
              <p>
                Save hours of reading and explore expert-curated summaries,
                articles, and detailed infographics in Olympic weightlifting and
                other strength sports.
              </p>
              <p>
                →{" "}
                <FreeSectionLink href={links.blog}>
                  Go to the blog
                </FreeSectionLink>
              </p>
            </ResourceBlock>

            <ResourceBlock>
              <h3>Instagram</h3>
              <p>
                Follow us on Instagram for bite-sized, evidence-based insights.
              </p>
              <p>
                →{" "}
                <FreeSectionLink href={links.instagram}>
                  Follow on Instagram
                </FreeSectionLink>
              </p>
            </ResourceBlock>
          </ColumnContent>
        </LeftColumn>

        <RightColumn>
          <ColumnContent>
            <h2>PREMIUM resources</h2>

            <FeaturedResourceBlock>
              <h3>Speaking & workshops</h3>
              <p>
                Bring evidence-based strength and weightlifting education to
                your gym, coaches, or athletes. Invite me for an in-person
                workshop, seminar, Q&A, or a remote session via Zoom.
              </p>
              <p>
                →{" "}
                <PremiumSectionLink href={links.speakingInvite}>
                  Invite me to speak
                </PremiumSectionLink>
              </p>
            </FeaturedResourceBlock>

            <ResourceBlock>
              <h3>Exclusive access</h3>
              <ul>
                <li>
                  stay ahead with monthly{" "}
                  <strong>Olympic Weightlifting Research</strong> insights video
                </li>
                <li>
                  get the technical details, practical applications, and
                  real-life lifter examples — all from{" "}
                  <strong>extended expert interviews</strong> you can apply
                  right away
                </li>
                <li>
                  15+ hours of detailed{" "}
                  <strong>masterclass research breakdowns</strong>
                </li>
              </ul>
              <p>
                →{" "}
                <PremiumSectionLink href={links.patreon}>
                  Join on Patreon
                </PremiumSectionLink>
              </p>
            </ResourceBlock>

            <PatreonPreview
              href="https://www.patreon.com/posts/september-brief-140133971"
              target="_blank"
              rel="noopener noreferrer"
            >
              <PatreonPreviewImage
                src={patreonPreview}
                alt="September brief preview on Patreon"
              />
            </PatreonPreview>
          </ColumnContent>
        </RightColumn>
      </MainContent>
    </HomePageLayout>
  )
}

export default HomePage
